import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { Activity } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type IncidentMetricSeries,
  type IncidentMetricSeriesResponse,
  useIncidentMetricSeries,
} from '@/hooks/useIncidents';

/**
 * The metric behind a metric-driven incident, from ten minutes before it opened
 * to now (or to when it resolved).
 *
 * This section exists because the number used to live in the incident's
 * description, where the detector rewrote it on every evaluation: one
 * nine-hour production incident collected 97 `field_changed` timeline events,
 * each recording the same fact at a slightly different value. A description
 * answers "what rule broke"; only a series answers "is it getting worse", which
 * is the question a reader actually arrives with.
 *
 * Renders nothing at all for incidents that have no metric — the endpoint 404s
 * for device audits and manual incidents, and the hook reports that as `null`.
 */
export function IncidentMetricChart({
  incidentId,
  live,
}: {
  incidentId: string;
  live: boolean;
}) {
  const { data, isLoading, isError } = useIncidentMetricSeries(incidentId, live);

  if (isLoading) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <h2 className="text-sm font-semibold mb-4">Metric</h2>
        <Skeleton className="h-56 w-full" />
      </section>
    );
  }

  // No metric behind this incident: draw nothing rather than an empty frame.
  if (!data) return null;

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <header className="mb-1 flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          {data.metric}
        </h2>
        {live ? (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--sev-4)]"
              aria-hidden="true"
            />
            updating every minute
          </span>
        ) : null}
      </header>
      <p className="text-xs text-muted-foreground mb-4">{caption(data)}</p>

      {isError ? (
        <p className="text-sm text-muted-foreground">
          The metric store did not answer. The incident itself is unaffected.
        </p>
      ) : data.referenceMissing ? (
        <p className="text-sm text-muted-foreground">
          {data.metric} is being collected, but the reference it is measured against
          is not — so no percentage can be computed. The same gap stops this alert
          from evaluating on this device.
        </p>
      ) : data.series.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No samples in this window. For an alert that fires on missing data, that
          absence is the finding.
        </p>
      ) : (
        <Chart data={data} />
      )}
    </section>
  );
}

/**
 * Says what the chart is showing before the reader has to infer it: the rule,
 * whether the lines were selected or are a fallback, and — when the range was
 * long enough to bucket — that each point is a bucket rather than a sample.
 */
function caption(data: IncidentMetricSeriesResponse): string {
  const parts: string[] = [];

  if (data.conditionType === 'absence') {
    parts.push(`Alerts when no data arrives for ${String(data.windowMinutes)} minutes.`);
  } else if (data.conditionType === 'delta') {
    parts.push(`Alerts on change over ${String(data.windowMinutes)} minutes, not on the level.`);
  } else if (data.threshold !== null && data.operator) {
    parts.push(`Threshold ${data.operator} ${formatValue(data.threshold, data.unit)}.`);
  }

  if (data.series.length > 1) {
    parts.push(
      data.noBreach
        ? `${String(data.series.length)} of ${String(data.series.length + data.omittedSeries)} series, none currently over the threshold — the closest are shown.`
        : `${String(data.series.length)} breaching series shown${data.omittedSeries > 0 ? `, ${String(data.omittedSeries)} more not drawn` : ''}.`
    );
  } else if (data.noBreach && data.conditionType !== 'absence' && data.conditionType !== 'delta') {
    parts.push('Nothing in this window is over the threshold.');
  }

  if (data.bucketSeconds && data.bucketFunction) {
    parts.push(
      `Each point is the ${data.bucketFunction === 'min' ? 'lowest' : 'highest'} reading in ${formatDuration(data.bucketSeconds)}.`
    );
  }

  return parts.join(' ');
}

/**
 * One row per timestamp across every series, because the tooltip reads every
 * series at the pointer's X and a per-series shape cannot answer that.
 *
 * A `null` here means one of two things and the chart treats them alike: this
 * series has no reading at a timestamp another series does, or the backend
 * marked the bucket empty. Both are "no value known", and both break the line
 * rather than dipping it to zero.
 */
type ChartRow = { ts: number } & Record<string, number | null>;

function toRows(series: IncidentMetricSeries[]): ChartRow[] {
  const byTs = new Map<number, ChartRow>();
  series.forEach((s, index) => {
    for (const [ts, value] of s.points) {
      let row = byTs.get(ts);
      if (!row) {
        row = { ts };
        byTs.set(ts, row);
      }
      // A null arrives as a null and stays one. The store is telling us it does
      // not know what happened in this bucket, which is not the same as telling
      // us nothing happened.
      row[`s${String(index)}`] = value;
    }
  });
  return [...byTs.values()].sort((a, b) => a.ts - b.ts);
}

function Chart({ data }: { data: IncidentMetricSeriesResponse }) {
  const rows = useMemo(() => toRows(data.series), [data.series]);
  const spansDays = data.end - data.start > 24 * 3600 * 1000;
  const showLegend = data.series.length > 1;

  // The band past the threshold, tinted so "over" is legible without reading
  // the axis. Bounded by the data's own extreme rather than left open, because
  // an unbounded ReferenceArea rescales the axis to infinity.
  const extreme = useMemo(() => {
    // Gaps are excluded rather than counted as zero, which would stretch the
    // breach band down to a floor no reading ever touched.
    const values = data.series.flatMap((s) =>
      s.points.flatMap(([, v]) => (v === null ? [] : [v]))
    );
    if (values.length === 0) return null;
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [data.series]);

  const breachBand =
    data.threshold !== null && data.operator && extreme
      ? data.operator.startsWith('<')
        ? { y1: Math.min(extreme.min, data.threshold), y2: data.threshold }
        : { y1: data.threshold, y2: Math.max(extreme.max, data.threshold) }
      : null;

  return (
    <div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={rows} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />

          {breachBand ? (
            <ReferenceArea
              y1={breachBand.y1}
              y2={breachBand.y2}
              fill="var(--sev-1)"
              fillOpacity={0.06}
              ifOverflow="extendDomain"
            />
          ) : null}

          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={[data.start, data.end]}
            tickFormatter={(ts: number) => format(new Date(ts), spansDays ? 'MMM d HH:mm' : 'HH:mm')}
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            minTickGap={48}
          />
          <YAxis
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            width={64}
            tickFormatter={(v: number) => formatValue(v, data.unit)}
          />

          {/* The moment the incident opened. Everything left of it is the
              lead-in — the rise that produced the alert. */}
          <ReferenceLine
            x={data.incidentStarted}
            stroke="var(--fg-subtle)"
            strokeWidth={1}
            label={{
              value: 'opened',
              position: 'insideTopLeft',
              fontSize: 10,
              fill: 'var(--fg-subtle)',
            }}
          />

          {data.threshold !== null && data.conditionType !== 'delta' ? (
            <ReferenceLine
              y={data.threshold}
              stroke="var(--sev-1)"
              strokeDasharray="6 4"
              strokeWidth={1.5}
              label={{
                value: `threshold ${formatValue(data.threshold, data.unit)}`,
                position: 'insideBottomRight',
                fontSize: 10,
                fill: 'var(--fg-subtle)',
              }}
            />
          ) : null}

          <Tooltip
            cursor={{ stroke: 'var(--fg-subtle)', strokeWidth: 1 }}
            content={<SeriesTooltip data={data} spansDays={spansDays} />}
          />

          {data.series.map((s, index) => (
            <Line
              key={s.label || index}
              type="monotone"
              dataKey={`s${String(index)}`}
              stroke={seriesColor(index)}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
              // Deliberately NOT connectNulls. The backend only emits a null
              // where the store reported an empty bucket — a missed poll, or an
              // interval whose only reading was a counter reset — so bridging it
              // would draw a rate nobody observed. A break is the honest mark.
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {showLegend ? (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          {data.series.map((s, index) => (
            <li key={s.label || index} className="flex items-center gap-1.5 text-xs">
              <span
                className="inline-block w-3 h-0.5 rounded-full shrink-0"
                style={{ backgroundColor: seriesColor(index) }}
                aria-hidden="true"
              />
              <span className="text-foreground">{s.label || data.metric}</span>
              {s.peak !== null ? (
                <span className="text-muted-foreground">
                  peak {formatValue(s.peak, data.unit)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Eight fixed slots, assigned in order and never cycled — the order is what
 * keeps the palette readable under colour-vision deficiency. The backend caps
 * the response at eight series, so the fallback is unreachable in practice and
 * exists only so a cap change cannot silently produce an invisible line.
 */
function seriesColor(index: number): string {
  return index < 8 ? `var(--series-${String(index + 1)})` : 'var(--fg-subtle)';
}

/** Value leads, series name follows: the reader already has the series. */
function SeriesTooltip({
  active,
  payload,
  label,
  data,
  spansDays,
}: {
  active?: boolean;
  payload?: { dataKey?: string | number; value?: number | null }[];
  label?: number;
  data: IncidentMetricSeriesResponse;
  spansDays: boolean;
}) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="rounded-md border border-border bg-popover text-popover-foreground px-3 py-2 shadow-md">
      <p className="text-[11px] text-muted-foreground mb-1.5">
        {typeof label === 'number'
          ? format(new Date(label), spansDays ? 'MMM d, HH:mm' : 'HH:mm:ss')
          : ''}
      </p>
      <ul className="space-y-1">
        {payload.map((entry) => {
          const index = Number(String(entry.dataKey ?? '').replace('s', ''));
          if (index < 0 || index >= data.series.length) return null;
          // A gap has no value to report; the row is omitted rather than shown
          // as zero.
          if (entry.value === undefined || entry.value === null) return null;
          const series = data.series[index];
          return (
            <li key={String(entry.dataKey)} className="flex items-baseline gap-2 text-xs">
              <span
                className="inline-block w-3 h-0.5 rounded-full shrink-0"
                style={{ backgroundColor: seriesColor(index) }}
                aria-hidden="true"
              />
              <span className="font-semibold tabular-nums">
                {formatValue(entry.value, data.unit)}
              </span>
              <span className="text-muted-foreground">{series.label || data.metric}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Compact enough for an axis tick, exact enough to compare with a threshold. */
function formatValue(value: number, unit: string | null): string {
  switch (unit) {
    case 'percent':
      return `${trim(value)}%`;
    case 'bytes':
      return formatBytes(value);
    case 'bytes_per_second':
      return `${formatBytes(value)}/s`;
    case 'per_second':
      return `${trim(value)}/s`;
    case 'cores':
      return `${value.toFixed(2)} cores`;
    default:
      return trim(value);
  }
}

function trim(value: number): string {
  if (Math.abs(value) >= 1000) return Math.round(value).toLocaleString();
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1);
}

function formatBytes(value: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let scaled = Math.abs(value);
  let unitIndex = 0;
  while (scaled >= 1024 && unitIndex < units.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }
  const sign = value < 0 ? '-' : '';
  return `${sign}${scaled >= 100 || unitIndex === 0 ? Math.round(scaled).toString() : scaled.toFixed(1)} ${units[unitIndex]}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.round(seconds / 60))} min`;
  return `${String(Math.round(seconds / 360) / 10)} h`;
}
